import { Injectable } from '@angular/core';
import type { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, Observable, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Preloads lazy routes during browser idle time instead of immediately after the
 * first navigation.
 *
 * `PreloadAllModules` starts downloading every lazy chunk as soon as the first route
 * resolves, which competes for bandwidth and main-thread time with the initial page's
 * own data requests and rendering. On a media-heavy page that is exactly the wrong
 * moment to fetch code the user may never reach.
 *
 * Routes opt out with `data: { preload: false }` - use that for large, rarely visited
 * areas (admin, settings) whose chunks are not worth speculative download.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
  /** Fallback delay when requestIdleCallback is unavailable (Safari). */
  private static readonly FALLBACK_DELAY_MS = 2_000;

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] === false) {
      return EMPTY;
    }

    return this.whenIdle().pipe(mergeMap(() => load()));
  }

  private whenIdle(): Observable<unknown> {
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;

    if (typeof idle !== 'function') {
      return timer(IdlePreloadStrategy.FALLBACK_DELAY_MS);
    }

    return new Observable<void>((subscriber) => {
      idle(() => {
        subscriber.next();
        subscriber.complete();
      });
    });
  }
}
